// Browser devtools surfaces:
//   - `window.__magviz` handle for console poking (installDevtools)
//   - Redux DevTools extension bridge for the intent bus (installReduxDevtools)
// Kept in one file so the dev-only global-mutation points are searchable; the
// corresponding Window types live in src/types/global.d.ts (auto-included as
// an ambient declaration).

import { INTENTS, type IntentName } from '@store/intents';
import type { Store } from '@store/store';
import type { State } from '@store/schema';
import type { ViewerContext } from './context';

export function installDevtools(store: Store<State>, viewer: ViewerContext): void {
    window.__magviz = { store, viewer };
}

// Intents we tag with @@noisy/ so the extension's filter box can hide them
// (use `!@@noisy` in the filter). These fire on every mousemove/hover/refresh.
const NOISY: ReadonlySet<string> = new Set<string>([
    INTENTS.UI_HOVER_SHOW,
    INTENTS.UI_HOVER_HIDE,
    INTENTS.UI_PANE_REFRESH,
]);

// Bridge dispatches to the Redux DevTools browser extension. No-op when the
// extension is not installed. Must be called AFTER registerAppIntents so the
// snapshot handler iterates last (Sets preserve insertion order) and observes
// post-effect state.
export function installReduxDevtools(store: Store<State>): () => void {
    const ext = typeof window !== 'undefined' ? window.__REDUX_DEVTOOLS_EXTENSION__ : undefined;
    if (!ext) return () => {};

    const conn = ext.connect({ name: 'magviz' });
    conn.init(store.state);

    const offs: Array<() => void> = [];
    for (const name of Object.values(INTENTS) as IntentName[]) {
        const type = NOISY.has(name) ? `@@noisy/${name}` : name;
        offs.push(store.onIntent(name, (payload: unknown) => {
            conn.send({ type, payload }, store.state);
        }));
    }

    return () => { for (const off of offs) off(); };
}
